import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "panel"]

  // open: true starts the drawer open — the builder preview uses it so a
  // placement toggle's effect on the step list is visible immediately after
  // each iframe reload.
  static values = { open: { default: false, type: Boolean } }

  connect() {
    // No focus steal: auto-opening inside the builder's preview iframe must
    // not yank the keyboard away from the settings rail beside it.
    if (this.openValue) this.open(null, { focus: false })
  }

  close(e) {
    e?.preventDefault()
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.keydown)

    this.backdropTarget.classList.add("opacity-0")
    this.panelTarget.classList.add("translate-x-full")

    setTimeout(() => {
      this.backdropTarget.classList.add("hidden")
      this.panelTarget.classList.add("hidden")
    }, 300)

    this.previouslyFocused?.focus()
    this.previouslyFocused = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
  }

  // The drawer is modal (backdrop + scroll lock), so while it is open Escape
  // closes it and Tab cycles inside the panel instead of reaching the
  // obscured page behind the backdrop.
  keydown = (e) => {
    if (e.key === "Escape") {
      this.close(e)
      return
    }

    if (e.key !== "Tab") return

    const focusables = this.panelTarget.querySelectorAll("a[href], button")
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  open(e, { focus = true } = {}) {
    e?.preventDefault()
    this.previouslyFocused = document.activeElement
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.keydown)

    this.backdropTarget.classList.remove("hidden")
    this.panelTarget.classList.remove("hidden")

    setTimeout(() => {
      this.backdropTarget.classList.remove("opacity-0")
      this.panelTarget.classList.remove("translate-x-full")
      if (focus) this.panelTarget.focus()
    }, 5)
  }
}
